import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CardTitleRow from './CardTitleRow';
import LoadErrorScreen from './LoadErrorScreen';
import PhoneIcon from '@mui/icons-material/Phone';
import './ItemCard.css';
import './ItemList.css';
import { getProductDeliveryCaption } from '../utils/productDelivery';

// Подпись на карточке: верхний левый угол, закруглённый фон
const CardLabel = ({ label, bgColor, color = '#000' }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 8,
      left: 8,
      zIndex: 1,
      backgroundColor: bgColor,
      color,
      px: 1,
      py: 0.25,
      borderRadius: '8px',
      fontSize: '0.75rem',
      fontWeight: 600,
    }}
  >
    {label}
  </Box>
);

const PAGE_SIZE_INITIAL = 24;
const PAGE_SIZE_NEXT = 48;

const buildFeedParams = (locationSearch, { cursor = null, limit = null, includePinned = false, excludeIds = [] } = {}) => {
  const searchParams = new URLSearchParams(locationSearch);
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  const branchId = searchParams.get('branchId');
  const q = searchParams.get('q') || '';

  const params = new URLSearchParams();
  params.set('limit', String(limit != null ? limit : (cursor ? PAGE_SIZE_NEXT : PAGE_SIZE_INITIAL)));
  if (cursor) params.set('cursor', cursor);
  if (dateFrom) params.set('from', dateFrom);
  if (dateTo) params.set('to', dateTo);
  if (branchId) params.set('branchId', branchId);
  if (q.trim()) params.set('q', q.trim());
  if (includePinned) params.set('includePinned', 'true');
  else params.set('includePinned', 'false');
  if (excludeIds.length) params.set('excludeIds', excludeIds.join(','));

  return params;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getPhotoUrl = (photo) => {
  if (!photo) return null;
  if (typeof photo === 'string') {
    const trimmed = photo.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('/')) return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
    return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
  }
  return null;
};

const normalizePhotos = (photos) => {
  if (!photos || !Array.isArray(photos) || photos.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url && !seen.has(url)) {
      seen.add(url);
      out.push(url);
    }
  }
  return out;
};

const getFirstServicePhoto = (service) => {
  const photos = service.photos;
  if (!photos || !Array.isArray(photos)) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
};

const getFirstProductPhoto = (product) => {
  const photos = product.photos;
  if (!photos || !Array.isArray(photos)) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
};

const getFirstTourPhoto = (tour) => {
  const photos = tour.photos;
  if (!photos || !Array.isArray(photos)) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
};

// Лента формируется на сервере (/api/feed) с учётом популярности и пропорций типов карточек.

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [merged, setMerged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [branchAddressByKey, setBranchAddressByKey] = useState({});
  const [cartItemIds, setCartItemIds] = useState(new Set());
  const [cartProductIds, setCartProductIds] = useState(new Set());
  const [addingCartId, setAddingCartId] = useState(null);
  const loadMoreSentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const pinnedIdsRef = useRef([]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    const searchParams = new URLSearchParams(location.search);
    const legacySearch = (searchParams.get('search') || '').trim();
    if (!legacySearch) return;
    const params = new URLSearchParams(location.search);
    params.delete('search');
    params.set('q', legacySearch);
    navigate(`/search?${params.toString()}`, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    pinnedIdsRef.current = [];
    const token = localStorage.getItem('token');
    const searchParams = new URLSearchParams(location.search);
    const hasQuery = Boolean((searchParams.get('q') || '').trim());

    const fetchBranches = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`);
        if (!res.ok) return;
        const list = await res.json();
        const map = {};
        (list || []).forEach((b) => {
          const key = b.branchId || b._id;
          if (key) map[key] = [b.city, b.address].filter(Boolean).join(', ') || key;
        });
        if (!cancelled) setBranchAddressByKey(map);
      } catch (e) {
        console.error('Ошибка загрузки филиалов:', e);
      }
    };

    const fetchCart = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const itemIds = (data.items || [])
            .filter((i) => i.itemId)
            .map((i) => String(i.itemId._id || i.itemId));
          const productIds = (data.items || [])
            .filter((i) => i.productId)
            .map((i) => String(i.productId._id || i.productId));
          if (!cancelled) {
            setCartItemIds(new Set(itemIds));
            setCartProductIds(new Set(productIds));
          }
        }
      } catch (e) {
        console.error('Ошибка загрузки корзины:', e);
      }
    };

    const loadPinned = async () => {
      if (hasQuery) return [];
      try {
        const params = buildFeedParams(location.search);
        params.delete('includePinned');
        params.delete('cursor');
        params.delete('limit');
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/feed/pinned?${params.toString()}`,
          { headers: authHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        const entries = Array.isArray(data.entries) ? data.entries : [];
        if (!cancelled && entries.length) {
          pinnedIdsRef.current = entries
            .map((e) => e?.data?._id)
            .filter(Boolean)
            .map(String);
          setMerged(entries);
          setLoading(false);
        }
        return entries;
      } catch (e) {
        console.error('Ошибка загрузки популярных карточек:', e);
        return [];
      }
    };

    const loadFeed = async (excludeIds = []) => {
      try {
        const params = buildFeedParams(location.search, {
          limit: PAGE_SIZE_INITIAL,
          includePinned: false,
          excludeIds,
        });
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/feed?${params.toString()}`,
          { headers: authHeaders() }
        );
        if (!res.ok) throw new Error('feed failed');
        const data = await res.json();
        const entries = Array.isArray(data.entries) ? data.entries : [];
        if (!cancelled) {
          setMerged((prev) => (excludeIds.length ? [...prev, ...entries] : entries));
          setNextCursor(data.nextCursor || null);
        }
      } catch (e) {
        console.error('Ошибка загрузки ленты:', e);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBranches();
    fetchCart();

    if (hasQuery) {
      loadFeed();
    } else {
      loadPinned().then((pinnedEntries) => {
        if (cancelled) return;
        const excludeIds = (pinnedEntries || [])
          .map((e) => e?.data?._id)
          .filter(Boolean)
          .map(String);
        loadFeed(excludeIds);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const fetchMoreItems = useCallback(async () => {
    if (!nextCursor || loadingMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const params = buildFeedParams(location.search, {
        cursor: nextCursor,
        limit: PAGE_SIZE_NEXT,
        includePinned: false,
        excludeIds: pinnedIdsRef.current,
      });
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/feed?${params.toString()}`,
        { headers: authHeaders() }
      );
      if (!response.ok) return;
      const data = await response.json();
      const newEntries = Array.isArray(data.entries) ? data.entries : [];
      setMerged((prev) => [...prev, ...newEntries]);
      setNextCursor(newEntries.length > 0 ? data.nextCursor || null : null);
    } catch (e) {
      console.error('Ошибка подгрузки ленты:', e);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, location.search]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !nextCursor) return;
    const rootMargin = isMobile ? '400px' : '200px';
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadingMoreRef.current) return;
        fetchMoreItems();
      },
      { rootMargin, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextCursor, loadingMore, fetchMoreItems, isMobile]);

  useEffect(() => {
    if (!isMobile || !nextCursor || loadingMoreRef.current) return;
    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight < 400) fetchMoreItems();
    };
    window.addEventListener('scroll', checkScroll, { passive: true });
    return () => window.removeEventListener('scroll', checkScroll);
  }, [isMobile, nextCursor, loadingMore, fetchMoreItems]);

  const handleAddToCart = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm('Для добавления в корзину нужно войти. Перейти на страницу входа?')) {
        navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
      }
      return;
    }
    if (addingCartId || cartItemIds.has(String(item._id))) return;
    setAddingCartId(item._id);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId: item._id, quantity: 1 }),
      });
      if (response.ok) {
        setCartItemIds((prev) => new Set([...prev, String(item._id)]));
      }
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
    } finally {
      setAddingCartId(null);
    }
  };

  const handleAddToCartProduct = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm('Для добавления в корзину нужно войти. Перейти на страницу входа?')) {
        navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
      }
      return;
    }
    if (addingCartId || cartProductIds.has(String(product._id))) return;
    setAddingCartId(product._id);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productNumber: product.productNumber, quantity: 1 }),
      });
      if (res.ok) setCartProductIds((prev) => new Set([...prev, String(product._id)]));
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
    } finally {
      setAddingCartId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError && merged.length === 0) {
    return <LoadErrorScreen />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', px: 0.25, mx: 0, mt: 2 }}>
      <Grid container spacing={1} className="items-grid-container" sx={{ px: 0.5, overflowX: 'hidden', maxWidth: '100%' }}>
        {merged.length > 0 ? (
          merged.map((entry, index) => {
            const isAboveFold = index < 8;
            if (entry.type === 'item') {
              const item = entry.data;
              const photos = normalizePhotos(item.photos);
              const photoUrl = photos[0] || 'https://via.placeholder.com/300';
              return (
                <Grid item xs={6} sm={6} md={4} lg={true} key={`item-${item._id}${entry.pinned ? '-pin' : ''}`} className="item-grid-item">
                  <Card component={Link} to={`/item/${item.inventoryNumber || item._id}`} className="card-container">
                    <Box sx={{ position: 'relative' }}>
                      <CardLabel label="Аренда" bgColor="#F9E79F" />
                      <IconButton
                        size="small"
                        onClick={(e) => handleAddToCart(e, item)}
                        disabled={!!addingCartId}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: '#fff' },
                        }}
                        aria-label="Добавить в корзину"
                      >
                        <ShoppingCartIcon
                          sx={{ color: cartItemIds.has(String(item._id)) ? '#d32f2f' : undefined }}
                        />
                      </IconButton>
                      <div className="slider-image-container">
                        <img
                          src={photoUrl}
                          alt={item.title || 'Товар'}
                          className="card-image"
                          loading={isAboveFold ? 'eager' : 'lazy'}
                          decoding="async"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300';
                          }}
                        />
                      </div>
                    </Box>
                    <CardContent className="card-content">
                      <CardTitleRow type="item" entityId={item._id}>
                        {item.title}
                      </CardTitleRow>
                      <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                        {item.price} ₽/сутки
                      </Typography>
                      {item.branchId && branchAddressByKey[item.branchId] && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}>
                          📍 {branchAddressByKey[item.branchId]}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            }
            if (entry.type === 'product') {
              const product = entry.data;
              const photoUrl = getFirstProductPhoto(product) || 'https://via.placeholder.com/300';
              const deliveryCaption = getProductDeliveryCaption(product, branchAddressByKey);
              return (
                <Grid item xs={6} sm={6} md={4} lg={true} key={`product-${product._id}${entry.pinned ? '-pin' : ''}`} className="item-grid-item">
                  <Card
                    component={Link}
                    to={`/products/${product.productNumber}`}
                    className="card-container"
                    sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardLabel label="Продажа" bgColor="#2e7d32" color="#fff" />
                      <IconButton
                        size="small"
                        onClick={(e) => handleAddToCartProduct(e, product)}
                        disabled={!!addingCartId}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: '#fff' },
                        }}
                        aria-label="В корзину"
                      >
                        <ShoppingCartIcon sx={{ color: cartProductIds.has(String(product._id)) ? '#d32f2f' : undefined }} />
                      </IconButton>
                      <div className="slider-image-container">
                        <img
                          src={photoUrl}
                          alt={product.title || 'Товар'}
                          className="card-image"
                          loading={isAboveFold ? 'eager' : 'lazy'}
                          decoding="async"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                        />
                      </div>
                    </Box>
                    <CardContent className="card-content">
                      <CardTitleRow type="product" entityId={product._id}>
                        {product.title}
                      </CardTitleRow>
                      <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                        {product.price != null ? `${Number(product.price).toLocaleString('ru-RU')} ₽` : 'Цена по запросу'}
                      </Typography>
                      {deliveryCaption && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}>
                          {deliveryCaption}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            }
            if (entry.type === 'tour') {
              const tour = entry.data;
              const photoUrl = getFirstTourPhoto(tour) || 'https://via.placeholder.com/300';
              return (
                <Grid item xs={6} sm={6} md={4} lg={true} key={`tour-${tour._id || tour.tourNumber}`} className="item-grid-item">
                  <Card
                    component={Link}
                    to={`/tours/${tour.tourNumber}`}
                    className="card-container"
                    sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardLabel label="Тур" bgColor="#5B7DB1" color="#fff" />
                      <div className="slider-image-container">
                        <img
                          src={photoUrl}
                          alt={tour.title || 'Тур'}
                          className="card-image"
                          loading={isAboveFold ? 'eager' : 'lazy'}
                          decoding="async"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                        />
                      </div>
                    </Box>
                    <CardContent className="card-content">
                      <CardTitleRow type="tour" entityId={tour._id}>
                        {tour.title}
                      </CardTitleRow>
                      <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                        {tour.price != null ? `${Number(tour.price).toLocaleString('ru-RU')} ₽ / место` : 'Цена по запросу'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}>
                        {tour.destination || 'Направление уточняется'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            }
            const service = entry.data;
            const photoUrl = getFirstServicePhoto(service) || 'https://via.placeholder.com/300';
            const phone = (service.contactNumber || '').trim();
            const telHref = phone ? `tel:${phone.replace(/\s/g, '')}` : null;
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={`service-${service.serviceId}`} className="item-grid-item">
                <Card
                  component={Link}
                  to={`/services/${service.serviceId}`}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardLabel label="Услуга" bgColor="#EB984E" />
                    {telHref && (
                      <IconButton
                        component="a"
                        href={telHref}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: '#fff' },
                        }}
                        aria-label="Позвонить"
                      >
                        <PhoneIcon sx={{ color: '#2e7d32' }} />
                      </IconButton>
                    )}
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={service.name || 'Услуга'}
                        className="card-image"
                        loading={isAboveFold ? 'eager' : 'lazy'}
                        decoding="async"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300';
                        }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="service" entityId={service._id}>
                      {service.name}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      от {service.minPrice != null ? Number(service.minPrice).toLocaleString('ru-RU') : '—'} ₽
                    </Typography>
                    {phone && telHref && (
                      <Typography
                        component="a"
                        href={telHref}
                        variant="caption"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          mt: 0.5,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        📞 {phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : !loading ? (
          <Typography className="no-items-message">Пока ничего нет</Typography>
        ) : null}
        {nextCursor && merged.length > 0 && (
          <Grid item xs={12} sx={{ py: 0, px: 0, mx: 0 }}>
            <div ref={loadMoreSentinelRef} aria-hidden="true" style={{ height: 2, minHeight: 2 }} />
          </Grid>
        )}
        {loadingMore && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={32} sx={{ color: '#8B7D6B' }} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default HomePage;
